import { ConfirmationDialog, IconButton, Trigger, useHasPermission } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { PermissionName } from "../../../../shared/types/permission";
import { companyService } from "../../shared/api/companyService";
import { TenantArchiveDrawer } from "../../shared/tenantArchive/TenantArchiveDrawer";
import { useArchiveByTenantHandler } from "../../shared/tenantArchive/useRestoreByTenantHandler";
import { ICompanyUpdateResource } from "../../shared/types";
import {
	ViewPageSection,
	ViewPageSectionBody,
	ViewPageSectionHeader,
	ViewPageSectionListItem,
} from "../../shared/view";
import { CompanyTestDriveLinks } from "./CompanyTestDriveLinks";
import { CompanyViewTenantPivotSection } from "./CompanyViewTenantPivotSection";
import { CompanyViewUpdateDrawer } from "./CompanyViewUpdateDrawer";

interface Props {
	company: ICompanyUpdateResource;
}

const CompanyPageSectionHeader = styled(ViewPageSectionHeader)`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(1)};
`;

const HeaderContent = styled.div`
	display: flex;
	gap: ${({ theme }) => theme.spacing(1)};
	align-items: center;
`;

function CompanyArchiveButton(props: { company: ICompanyUpdateResource }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		clickHandler: archiveClickHandler,
		confirmationProps,
		drawerProps,
	} = useArchiveByTenantHandler(companyService, () => {
		navigate(-1);
	});

	return (
		<>
			<IconButton
				title={t("Archive")}
				icon="archive_2"
				type="button"
				size="small"
				onClick={() => archiveClickHandler(props.company.id)}
			/>
			<ConfirmationDialog
				{...confirmationProps}
				heading={t("Archive")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
			>
				{t("Are you sure you want to archive this company?")}
			</ConfirmationDialog>
			<TenantArchiveDrawer {...drawerProps} title={t("Archive company")} type="archive" service={companyService} />
		</>
	);
}

export const CompanyViewGeneralSection: FC<Props> = ({ company }) => {
	const { t } = useTranslation();
	const canArchive = useHasPermission<PermissionName>(["company.archive"]);

	return (
		<ViewPageSection style={{ gridArea: "general" }}>
			<CompanyPageSectionHeader>
				{t("General")}
				<HeaderContent>
					<CompanyTestDriveLinks companyId={company.id} />
					{canArchive && <CompanyArchiveButton company={company} />}
				</HeaderContent>
			</CompanyPageSectionHeader>
			<ViewPageSectionBody>
				<Trigger render={(props) => <CompanyViewUpdateDrawer {...props} company={company} />}>
					{(props) => (
						<ViewPageSectionListItem {...props}>
							<div>{company.name}</div>
						</ViewPageSectionListItem>
					)}
				</Trigger>
				<CompanyViewTenantPivotSection company={company} />
			</ViewPageSectionBody>
		</ViewPageSection>
	);
};
